import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useState, useEffect, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import {
    fetchPublicaciones,
    Publicacion
} from '../../../services/blogPublicaciones';
import AddBlogPublicaciones from '../../../components/BlogPublicaciones/AddBlogPublicaciones';
import EditBlogPublicaciones from '../../../components/BlogPublicaciones/EditBlogPublicaciones';
import DeleteBlogPublicaciones from '../../../components/BlogPublicaciones/DeleteBlogPublicaciones';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const BlogPublicacionesPage: React.FC = () => {
    const [publicaciones, setPublicaciones] = useState<Publicacion[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPublicacion, setSelectedPublicacion] = useState<Publicacion | null>(null);

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();

    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('publicacionblog', userLevelId); // Ajusta el nombre del modelo según tus permisos

    const loadPublicaciones = useCallback(async () => {
        if (!perm?.can_read) {
            setPublicaciones([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await fetchPublicaciones(); // Puedes agregar parámetros si deseas filtros iniciales
            setPublicaciones(data);
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm?.can_read]);

    useEffect(() => {
        loadPublicaciones();
    }, [loadPublicaciones]);

    const totalPages = Math.ceil(publicaciones.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedPublicaciones = publicaciones.slice(startIndex, startIndex + itemsPerPage);

    const handleRefresh = () => loadPublicaciones();

    const handleAddClick = () => setShowAddModal(true);

    const handleEditClick = (pub: Publicacion) => {
        setSelectedPublicacion(pub);
        setShowEditModal(true);
    };

    const handleDeleteClick = (pub: Publicacion) => {
        setSelectedPublicacion(pub);
        setShowDeleteModal(true);
    };

    const handleModalSuccess = () => loadPublicaciones();

    if (perm === null || (loading && publicaciones.length === 0)) return <Loading text="Cargando publicaciones..." />;

    return <><LumaResourceWorkspace title="Publicaciones de blog" description="Consulta y gestiona la información de tu coworking." group="Contenido" rows={publicaciones} columns={[{key:'titulo',label:'Título'},{key:'subtitulo',label:'Subtítulo'},{key:'categoria',label:'Categoría'},{key:'estado',label:'Estado'},{key:'fecha_publicacion',label:'Fecha de publicación'}]} idKey="id" nameKey="titulo" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{handleAddClick()}:undefined} onEdit={perm?.can_update?handleEditClick:undefined} onDelete={perm?.can_delete?handleDeleteClick:undefined}></LumaResourceWorkspace><ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />{showAddModal && (
                    <AddBlogPublicaciones
                        isOpen={showAddModal}
                        onClose={() => setShowAddModal(false)}
                        onSuccess={handleModalSuccess}
                    />
                )}{showEditModal && selectedPublicacion && (
                    <EditBlogPublicaciones
                        isOpen={showEditModal}
                        publicacion={selectedPublicacion}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedPublicacion(null);
                        }}
                        onSuccess={handleModalSuccess}
                    />
                )}{showDeleteModal && selectedPublicacion && (
                    <DeleteBlogPublicaciones
                        isOpen={showDeleteModal}
                        publicacion={selectedPublicacion}
                        onClose={() => {
                            setShowDeleteModal(false);
                            setSelectedPublicacion(null);
                        }}
                        onSuccess={handleModalSuccess}
                    />
                )}</>;
};

export default BlogPublicacionesPage;

